/**
 * Statistic / counter block. design.md 5.8, and 7.5 for the animation.
 *
 * The value is the exact source string: "142135" renders as `142135`, with no
 * thousands separator, because the brief classes the counters as verbatim
 * content. Grouping would be a content change and would have to be logged.
 *
 * One `requestAnimationFrame` loop drives every value in a row, so the four
 * homepage figures start together and finish together and read as one
 * measurement rather than a fireworks sequence. A row that is already on
 * screen at mount, a reader with reduced motion, a browser without
 * `IntersectionObserver` and a build with COUNT_UP off all render the final
 * values at first paint.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { CounterBlockData } from '@/lib/content';
import { COUNT_UP } from '@/lib/switches';

/** 7.2 `--dur-count`. */
const DURATION_MS = 1200;
/** If frames are throttled (hidden tab), show the numbers anyway. */
const SAFETY_MS = 3000;

export type StatItem = {
  value: string;
  label: string;
  /** Optional "Quelle: …" line (5.8); mandatory only in the single variant. */
  source?: string;
};

type RowProps = {
  items: StatItem[];
  className?: string;
  /** Wrapper element, so a row can be a list where that is the right shape. */
  reveal?: boolean;
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The numeric target of a source value, or null when it is not a number. */
function targetOf(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const n = Number(value.trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * A row of statistics with one shared count-up.
 *
 * `progress` is null until the row is armed, then 0 → 1. While it is null the
 * final value is what renders, so the number is never left at zero.
 */
export function StatRow({ items, className = 'hp-counters', reveal = false }: RowProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!COUNT_UP) return;
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return;

    // Already on screen at mount (deep link, restored scroll): print them.
    const box = node.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    if (box.top < vh && box.bottom > 0) return;

    let frame = 0;
    let started = false;

    const run = () => {
      started = true;
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / DURATION_MS);
        // Ease-out cubic: precise, no overshoot.
        setProgress(1 - Math.pow(1 - t, 3));
        if (t < 1) frame = requestAnimationFrame(step);
        else setProgress(null);
      };
      setProgress(0);
      frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        run();
      },
      { threshold: 0.5 },
    );
    observer.observe(node);

    const safety = window.setTimeout(() => {
      if (started) return;
      observer.disconnect();
      setProgress(null);
    }, SAFETY_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(safety);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [items.length]);

  return (
    <div className={className} ref={ref} data-reveal-group={reveal ? '' : undefined}>
      {items.map((item, index) => (
        <Stat item={item} progress={progress} index={index} key={`${item.label}-${index}`} />
      ))}
    </div>
  );
}

function Stat({
  item,
  progress,
  index,
}: {
  item: StatItem;
  progress: number | null;
  index: number;
}) {
  const target = targetOf(item.value);
  const animating = progress !== null && target !== null;
  const display = animating ? String(Math.round(target * progress)) : item.value;

  return (
    <div className="hp-block hp-counter" style={{ '--i': index } as CSSProperties}>
      <span className="hp-counter__value tabular-nums" aria-hidden="true">
        {display}
      </span>
      <span className="hp-visually-hidden">{item.value}</span>
      <span className="hp-counter__label">{item.label}</span>
      {item.source ? <span className="hp-counter__source">{item.source}</span> : null}
    </div>
  );
}

/** The generic block-stream form: one source `counter` block on its own. */
export default function CounterBlock({ block }: { block: CounterBlockData }) {
  return <StatRow items={[{ value: block.value, label: block.label }]} />;
}
